import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

// Allow class attributes (needed for syntax-highlighted code blocks via remark-gfm)
// while still stripping <script>, event handlers, javascript: URLs, etc.
const markdownSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), ["className", /^language-./]],
    span: [...(defaultSchema.attributes?.span || []), ["className"]],
  },
};
import { Header } from "../components/layout/Header";
import { useAuth } from "../hooks/useAuth";
import {
  pluginApi,
  stripeApi,
  type Plugin,
  type PluginFile,
  type PluginFileMetadata,
} from "../lib/api";
import {
  ArrowLeftIcon,
  DownloadIcon,
  StarIcon,
  ExternalLinkIcon,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
  CopyIcon,
  SparklesIcon,
  TerminalIcon,
  BotIcon,
  ZapIcon,
  PlugIcon,
  XIcon,
  GridIcon,
  ListIcon,
  ForkIcon,
  PlusIcon,
  UploadIcon,
  TrashIcon,
  GitHubIcon,
  InfoIcon,
} from "../components/ui/Icons";

type FileTreeNode = {
  name: string;
  type: "folder" | "file";
  path: string;
  file?: PluginFile;
  children?: FileTreeNode[];
};

// Helper to safely render metadata values that might be objects
function safeString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // For objects or arrays, return null to prevent React Error #31
  return null;
}

type Tab = "overview" | "files";

function buildFileTree(files: PluginFile[]): { tree: FileTreeNode[]; readme: PluginFile | null } {
  const root: FileTreeNode[] = [];
  let readme: PluginFile | null = null;

  const sortedFiles = [...files].sort((a, b) => {
    const aIsReadme = a.fileName.toLowerCase().includes("readme");
    const bIsReadme = b.fileName.toLowerCase().includes("readme");
    if (aIsReadme && !bIsReadme) return -1;
    if (!aIsReadme && bIsReadme) return 1;
    return a.fileName.localeCompare(b.fileName);
  });

  for (const file of sortedFiles) {
    const parts = file.fileName.split("/");

    if (file.fileName.toLowerCase().includes("readme") && !readme) {
      readme = file;
    }

    if (parts.length === 1) {
      root.push({
        name: parts[0],
        type: "file",
        path: file.fileName,
        file,
      });
    } else {
      let current = root;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLast = i === parts.length - 1;
        const currentPath = parts.slice(0, i + 1).join("/");

        if (isLast) {
          current.push({
            name: part,
            type: "file",
            path: file.fileName,
            file,
          });
        } else {
          let folder = current.find((n) => n.type === "folder" && n.name === part);
          if (!folder) {
            folder = {
              name: part,
              type: "folder",
              path: currentPath,
              children: [],
            };
            current.push(folder);
          }
          current = folder.children!;
        }
      }
    }
  }

  const sortLevel = (nodes: FileTreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.type === "file" && b.type === "file") {
        const aIsReadme = a.name.toLowerCase().includes("readme");
        const bIsReadme = b.name.toLowerCase().includes("readme");
        if (aIsReadme && !bIsReadme) return -1;
        if (!aIsReadme && bIsReadme) return 1;
      }
      if (a.type !== b.type) {
        return a.type === "folder" ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    nodes.forEach((node) => {
      if (node.children) sortLevel(node.children);
    });
  };

  sortLevel(root);
  return { tree: root, readme };
}

// Strip YAML frontmatter from markdown content
function stripFrontmatter(content: string): string {
  if (!content.startsWith("---")) return content;
  const endIndex = content.indexOf("---", 3);
  if (endIndex === -1) return content;
  return content.slice(endIndex + 3).trim();
}

export function PluginDetailPage() {
  const { slug, version: urlVersion } = useParams<{ slug: string; version?: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [plugin, setPlugin] = useState<Plugin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [isForking, setIsForking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Favorite state
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // File explorer state
  const [selectedFile, setSelectedFile] = useState<PluginFile | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Slideout state
  const [slideoutFile, setSlideoutFile] = useState<PluginFile | null>(null);
  const [slideoutContent, setSlideoutContent] = useState<string | null>(null);
  const [loadingSlideout, setLoadingSlideout] = useState(false);

  // README state
  const [readmeContent, setReadmeContent] = useState<string | null>(null);

  const [copied, setCopied] = useState<string | null>(null);

  // Add component modal state
  const [showAddModal, setShowAddModal] = useState(false);

  // Install modal state
  const [showInstallModal, setShowInstallModal] = useState(false);
  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Purchase state
  const [searchParams] = useSearchParams();
  const [isPurchased, setIsPurchased] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [selectedComponentType, setSelectedComponentType] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [referenceFiles, setReferenceFiles] = useState<File[]>([]);

  // Skill form
  const [skillForm, setSkillForm] = useState({
    name: "",
    description: "",
    allowedTools: [] as string[],
    model: "",
    content:
      "# Your Skill Name\n\nDescribe what this skill does and how Claude should use it.\n\n## Instructions\n\n- Step 1\n- Step 2\n- Step 3\n",
  });

  // Command form
  const [commandForm, setCommandForm] = useState({
    name: "",
    description: "",
    argumentHint: "",
    allowedTools: [] as string[],
    model: "",
    content:
      "Your command instructions here.\n\nUse $ARGUMENTS for all arguments or $1, $2 for individual arguments.\n",
  });

  // Agent form
  const [agentForm, setAgentForm] = useState({
    name: "",
    description: "",
    tools: [] as string[],
    model: "",
    permissionMode: "default",
    content:
      "You are a specialized agent.\n\n## Your Role\n\nDescribe what this agent should do.\n",
  });

  // Hook form
  const [hookForm, setHookForm] = useState({
    event: "PreToolUse",
    matcher: "",
    hookType: "command" as "command" | "prompt",
    command: "",
    prompt: "",
    timeout: 60000,
  });

  const AVAILABLE_TOOLS = [
    "Read",
    "Write",
    "Edit",
    "Glob",
    "Grep",
    "Bash",
    "Task",
    "WebFetch",
    "WebSearch",
    "TodoWrite",
    "AskUserQuestion",
  ];

  const HOOK_EVENTS = [
    { value: "PreToolUse", label: "PreToolUse", description: "Before Claude uses a tool" },
    { value: "PostToolUse", label: "PostToolUse", description: "After Claude uses a tool" },
    { value: "Stop", label: "Stop", description: "When main agent finishes" },
    { value: "SubagentStop", label: "SubagentStop", description: "When subagent finishes" },
    { value: "SessionStart", label: "SessionStart", description: "When session starts" },
    {
      value: "UserPromptSubmit",
      label: "UserPromptSubmit",
      description: "When user submits prompt",
    },
  ];

  useEffect(() => {
    if (!slug) return;

    const loadPlugin = async () => {
      try {
        const data = await pluginApi.getPlugin(slug);
        setPlugin(data);
        setFavoriteCount(data._count?.favorites || 0);

        // Fetch favorite status if authenticated
        if (isAuthenticated) {
          try {
            const status = await pluginApi.getFavoriteStatus(data.id);
            setIsFavorited(status.favorited);
          } catch {
            // Ignore favorite status fetch errors
          }
        }

        const { tree, readme } = buildFileTree(data.files || []);

        const allFolderPaths = new Set<string>();
        const collectFolders = (nodes: FileTreeNode[]) => {
          nodes.forEach((node) => {
            if (node.type === "folder") {
              allFolderPaths.add(node.path);
              if (node.children) collectFolders(node.children);
            }
          });
        };
        collectFolders(tree);
        setExpandedFolders(allFolderPaths);

        if (readme) {
          setSelectedFile(readme);
          loadFileContent(readme);
          // Load README content for overview display
          try {
            const response = await fetch(readme.s3Url);
            const text = await response.text();
            setReadmeContent(stripFrontmatter(text));
          } catch {
            // Ignore README load errors
          }
        } else if (data.files && data.files.length > 0) {
          const firstFile = data.files[0];
          setSelectedFile(firstFile);
          loadFileContent(firstFile);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load plugin");
      } finally {
        setIsLoading(false);
      }
    };

    loadPlugin();
  }, [slug, isAuthenticated]);

  // Check purchase status for paid plugins
  useEffect(() => {
    if (!plugin || !isAuthenticated || plugin.pricingModel === "FREE") return;
    const checkPurchase = async () => {
      try {
        const { purchased } = await stripeApi.getPurchaseStatus(plugin.id);
        setIsPurchased(purchased);
      } catch {
        // Ignore — user hasn't purchased
      }
    };
    checkPurchase();
  }, [plugin?.id, isAuthenticated, plugin?.pricingModel]);

  // Show success message after returning from Stripe checkout
  useEffect(() => {
    if (searchParams.get("purchased") === "true") {
      setShowPurchaseSuccess(true);
      setIsPurchased(true);
      const timer = setTimeout(() => setShowPurchaseSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleBuy = async () => {
    if (!plugin || !isAuthenticated) return;
    setIsBuying(true);
    try {
      const { url } = await stripeApi.createCheckout(plugin.id);
      window.location.href = url;
    } catch (err) {
      console.error("Failed to create checkout:", err);
      setIsBuying(false);
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const loadFileContent = async (file: PluginFile) => {
    setLoadingContent(true);
    try {
      const response = await fetch(file.s3Url);
      const text = await response.text();
      setFileContent(text);
    } catch {
      setFileContent("// Failed to load file content");
    } finally {
      setLoadingContent(false);
    }
  };

  const openSlideout = async (file: PluginFile) => {
    setSlideoutFile(file);
    setLoadingSlideout(true);
    try {
      const response = await fetch(file.s3Url);
      const text = await response.text();
      setSlideoutContent(stripFrontmatter(text));
    } catch {
      setSlideoutContent("Failed to load content");
    } finally {
      setLoadingSlideout(false);
    }
  };

  const closeSlideout = () => {
    setSlideoutFile(null);
    setSlideoutContent(null);
  };

  const handleFileClick = (file: PluginFile) => {
    setSelectedFile(file);
    loadFileContent(file);
  };

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
      case "APPROVED":
        return "bg-success/20 text-success ring-success/30";
      case "DRAFT":
        return "bg-warning/20 text-warning ring-warning/30";
      case "PENDING_REVIEW":
        return "bg-blue-500/20 text-blue-400 ring-blue-500/30";
      case "REJECTED":
        return "bg-danger/20 text-danger ring-danger/30";
      default:
        return "bg-muted/20 text-muted ring-muted/30";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "Published";
      case "APPROVED":
        return "Approved";
      case "DRAFT":
        return "Draft";
      case "PENDING_REVIEW":
        return "Pending Review";
      case "REJECTED":
        return "Rejected";
      default:
        return status;
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleFork = async () => {
    if (!plugin || isForking) return;

    setIsForking(true);
    try {
      const forkedPlugin = await pluginApi.forkPlugin(plugin.id);
      navigate(`/plugin/${forkedPlugin.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fork plugin");
      setIsForking(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!plugin || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const updatedPlugin = await pluginApi.submitForReview(plugin.id);
      setPlugin(updatedPlugin);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit for review");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canFork =
    plugin &&
    user &&
    (plugin.status === "APPROVED" || plugin.status === "PUBLISHED") &&
    plugin.author.id !== user.id;

  const isOwner = plugin && user && plugin.author.id === user.id;
  const isPaidPlugin = plugin?.pricingModel && plugin.pricingModel !== "FREE";

  const handleToggleFavorite = async () => {
    if (!plugin || !isAuthenticated || isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      const result = await pluginApi.toggleFavorite(plugin.id);
      setIsFavorited(result.favorited);
      setFavoriteCount(result.count);
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  // Install commands
  const marketplaceName = "supercharge-claude-code";
  const marketplaceUrl = "https://superchargeclaudecode.com/api/marketplace.json";
  const addMarketplaceCommand = `claude plugin marketplace add ${marketplaceUrl}`;
  const updateMarketplaceCommand = `claude plugin marketplace update ${marketplaceName}`;
  const installPluginCommand = plugin
    ? `claude plugin install ${plugin.slug}@${marketplaceName}`
    : "";
  const installCommand = plugin
    ? `claude plugin marketplace update ${marketplaceName} && claude plugin install ${plugin.slug}@${marketplaceName}`
    : "";

  const resetAddModal = () => {
    setShowAddModal(false);
    setSelectedComponentType(null);
    setReferenceFiles([]);
    setSkillForm({
      name: "",
      description: "",
      allowedTools: [],
      model: "",
      content:
        "# Your Skill Name\n\nDescribe what this skill does and how Claude should use it.\n\n## Instructions\n\n- Step 1\n- Step 2\n- Step 3\n",
    });
    setCommandForm({
      name: "",
      description: "",
      argumentHint: "",
      allowedTools: [],
      model: "",
      content:
        "Your command instructions here.\n\nUse $ARGUMENTS for all arguments or $1, $2 for individual arguments.\n",
    });
    setAgentForm({
      name: "",
      description: "",
      tools: [],
      model: "",
      permissionMode: "default",
      content:
        "You are a specialized agent.\n\n## Your Role\n\nDescribe what this agent should do.\n",
    });
    setHookForm({
      event: "PreToolUse",
      matcher: "",
      hookType: "command",
      command: "",
      prompt: "",
      timeout: 60000,
    });
  };

  const generateSkillFileContent = () => {
    const frontmatter = ["---", `name: ${skillForm.name}`, `description: ${skillForm.description}`];
    if (skillForm.allowedTools.length > 0) {
      frontmatter.push(`allowed-tools: ${skillForm.allowedTools.join(", ")}`);
    }
    if (skillForm.model) {
      frontmatter.push(`model: ${skillForm.model}`);
    }
    frontmatter.push("---", "");
    return frontmatter.join("\n") + skillForm.content;
  };

  const generateCommandFileContent = () => {
    const frontmatter = ["---"];
    if (commandForm.description) {
      frontmatter.push(`description: ${commandForm.description}`);
    }
    if (commandForm.argumentHint) {
      frontmatter.push(`argument-hint: ${commandForm.argumentHint}`);
    }
    if (commandForm.allowedTools.length > 0) {
      frontmatter.push(`allowed-tools: ${commandForm.allowedTools.join(", ")}`);
    }
    if (commandForm.model) {
      frontmatter.push(`model: ${commandForm.model}`);
    }
    frontmatter.push("---", "");
    return frontmatter.join("\n") + commandForm.content;
  };

  const generateAgentFileContent = () => {
    const frontmatter = ["---", `name: ${agentForm.name}`, `description: ${agentForm.description}`];
    if (agentForm.tools.length > 0) {
      frontmatter.push(`tools: ${agentForm.tools.join(", ")}`);
    }
    if (agentForm.model) {
      frontmatter.push(`model: ${agentForm.model}`);
    }
    if (agentForm.permissionMode && agentForm.permissionMode !== "default") {
      frontmatter.push(`permissionMode: ${agentForm.permissionMode}`);
    }
    frontmatter.push("---", "");
    return frontmatter.join("\n") + agentForm.content;
  };

  const generateHookFileContent = () => {
    const hook: Record<string, unknown> = { type: hookForm.hookType };
    if (hookForm.hookType === "command") {
      hook.command = hookForm.command;
    } else {
      hook.prompt = hookForm.prompt;
    }
    const config = {
      hooks: {
        [hookForm.event]: [
          {
            ...(hookForm.matcher && { matcher: hookForm.matcher }),
            hooks: [hook],
          },
        ],
      },
    };
    return JSON.stringify(config, null, 2);
  };

  const handleCreateComponent = async () => {
    if (!plugin || !selectedComponentType) return;

    const getName = () => {
      switch (selectedComponentType) {
        case "SKILL":
          return skillForm.name;
        case "COMMAND":
          return commandForm.name;
        case "AGENT":
          return agentForm.name;
        case "HOOK":
          return hookForm.event;
        default:
          return "";
      }
    };

    const name = getName();
    if (!name) return;

    setIsUploading(true);
    try {
      let content: string;
      let fileName: string;
      let fileType: PluginFile["fileType"];

      switch (selectedComponentType) {
        case "SKILL":
          content = generateSkillFileContent();
          fileName = `skills/${skillForm.name.toLowerCase().replace(/\s+/g, "-")}/SKILL.md`;
          fileType = "SKILL";
          break;
        case "COMMAND":
          content = generateCommandFileContent();
          fileName = `commands/${commandForm.name.toLowerCase().replace(/\s+/g, "-")}.md`;
          fileType = "COMMAND";
          break;
        case "AGENT":
          content = generateAgentFileContent();
          fileName = `agents/${agentForm.name.toLowerCase().replace(/\s+/g, "-")}.md`;
          fileType = "AGENT";
          break;
        case "HOOK":
          content = generateHookFileContent();
          fileName = "hooks.json";
          fileType = "HOOK";
          break;
        default:
          return;
      }

      const file = new File([content], fileName.split("/").pop() || "file.md", {
        type: selectedComponentType === "HOOK" ? "application/json" : "text/markdown",
      });

      await pluginApi.uploadFile(plugin.id, file, fileType, fileName);

      // Upload reference files if any
      for (const refFile of referenceFiles) {
        const refPath =
          selectedComponentType === "SKILL"
            ? `skills/${skillForm.name.toLowerCase().replace(/\s+/g, "-")}/${refFile.name}`
            : refFile.name;
        await pluginApi.uploadFile(plugin.id, refFile, "OTHER", refPath);
      }

      // Reload plugin to get updated files
      const updatedPlugin = await pluginApi.getPlugin(plugin.slug);
      setPlugin(updatedPlugin);
      resetAddModal();
    } catch (err) {
      console.error("Failed to create component:", err);
      setError(err instanceof Error ? err.message : "Failed to create component");
    } finally {
      setIsUploading(false);
    }
  };

  const handleReferenceFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setReferenceFiles([...referenceFiles, ...Array.from(e.target.files)]);
    }
  };

  const removeReferenceFile = (index: number) => {
    setReferenceFiles(referenceFiles.filter((_, i) => i !== index));
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!plugin || !confirm("Are you sure you want to delete this file?")) return;

    try {
      await pluginApi.deleteFile(plugin.id, fileId);
      const updatedPlugin = await pluginApi.getPlugin(plugin.slug);
      setPlugin(updatedPlugin);
    } catch (err) {
      console.error("Failed to delete file:", err);
      setError(err instanceof Error ? err.message : "Failed to delete file");
    }
  };

  const copyFileContent = () => {
    if (!fileContent) return;
    copyToClipboard(fileContent, "file");
  };

  const { tree: fileTree } = plugin ? buildFileTree(plugin.files || []) : { tree: [] };

  const fileTypeCounts =
    plugin?.files?.reduce(
      (acc, file) => {
        // Count all files of recognized types (not just those with descriptions)
        if (file.fileType !== "OTHER") {
          acc[file.fileType] = (acc[file.fileType] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>
    ) || {};

  const fileTypeConfig: Record<
    string,
    { label: string; color: string; bgColor: string; Icon: typeof SparklesIcon }
  > = {
    SKILL: {
      label: "skill",
      color: "text-purple-400",
      bgColor: "bg-purple-500/20 ring-purple-500/30",
      Icon: SparklesIcon,
    },
    COMMAND: {
      label: "command",
      color: "text-blue-400",
      bgColor: "bg-blue-500/20 ring-blue-500/30",
      Icon: TerminalIcon,
    },
    AGENT: {
      label: "agent",
      color: "text-amber-400",
      bgColor: "bg-amber-500/20 ring-amber-500/30",
      Icon: BotIcon,
    },
    HOOK: {
      label: "hook",
      color: "text-success",
      bgColor: "bg-success/20 ring-success/30",
      Icon: ZapIcon,
    },
    MCP_CONFIG: {
      label: "MCP config",
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/20 ring-cyan-500/30",
      Icon: PlugIcon,
    },
  };

  const filesByType =
    plugin?.files?.reduce(
      (acc, file) => {
        // Include all files of recognized types (not just those with descriptions)
        if (file.fileType !== "OTHER") {
          if (!acc[file.fileType]) acc[file.fileType] = [];
          acc[file.fileType].push(file);
        }
        return acc;
      },
      {} as Record<string, PluginFile[]>
    ) || {};

  const cardTypes = ["SKILL", "COMMAND", "AGENT", "HOOK"] as const;
  const hasCards = cardTypes.some((type) => filesByType[type]?.length > 0);

  const renderFileTree = (nodes: FileTreeNode[], depth = 0) => {
    return nodes.map((node) => {
      if (node.type === "folder") {
        const isExpanded = expandedFolders.has(node.path);
        return (
          <div key={node.path}>
            <button
              onClick={() => toggleFolder(node.path)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-secondary transition-colors hover:bg-surface hover:text-primary"
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
              {isExpanded ? (
                <>
                  <ChevronDownIcon size={14} className="shrink-0 text-muted" />
                  <FolderOpenIcon size={16} className="shrink-0 text-amber" />
                </>
              ) : (
                <>
                  <ChevronRightIcon size={14} className="shrink-0 text-muted" />
                  <FolderIcon size={16} className="shrink-0 text-amber" />
                </>
              )}
              <span className="truncate font-mono">{node.name}/</span>
              <span className="ml-auto shrink-0 font-mono text-xs text-muted">
                {node.children?.length || 0}
              </span>
            </button>
            {isExpanded && node.children && (
              <div className="border-l border-surface ml-4">
                {renderFileTree(node.children, depth + 1)}
              </div>
            )}
          </div>
        );
      }

      const file = node.file!;
      const isSelected = selectedFile?.id === file.id;

      return (
        <button
          key={file.id}
          onClick={() => handleFileClick(file)}
          className={`group flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
            isSelected
              ? "bg-amber/10 text-amber"
              : "text-secondary hover:bg-surface hover:text-primary"
          }`}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          <FileIcon
            size={14}
            className={`mt-0.5 shrink-0 ${isSelected ? "text-amber" : "text-muted"}`}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-xs">{node.name}</div>
            {file.description && (
              <div className="truncate text-xs text-muted group-hover:text-secondary">
                {file.description}
              </div>
            )}
          </div>
        </button>
      );
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <div className="flex items-center justify-center pt-40">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber border-t-transparent" />
            <p className="font-mono text-sm text-muted">Loading plugin...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !plugin) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <div className="flex flex-col items-center justify-center pt-40">
          <div className="card-glow max-w-md p-8 text-center">
            <h2 className="font-display text-xl font-bold text-primary">Plugin Not Found</h2>
            <p className="mt-2 text-secondary">
              {error || "This plugin doesn't exist or you don't have permission to view it."}
            </p>
            <Link to="/dashboard" className="btn-primary mt-6 inline-flex">
              <ArrowLeftIcon size={16} />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
      </div>

      {/* Plugin Header */}
      <div className="relative z-10 border-b border-surface bg-surface/80 backdrop-blur-sm pt-20">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <Link
                to="/dashboard"
                className="flex items-center justify-center rounded-lg p-2 text-secondary transition-colors hover:bg-surface hover:text-primary"
              >
                <ArrowLeftIcon size={18} />
              </Link>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber/20 to-amber-hover/20 ring-1 ring-amber/30">
                <span className="font-display text-lg font-bold text-amber">
                  {plugin.name.charAt(0).toUpperCase()}
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <h1 className="font-display text-lg font-bold text-primary">{plugin.name}</h1>
                  {isOwner && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ring-1 ${getStatusColor(plugin.status)}`}
                    >
                      {getStatusLabel(plugin.status)}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {/* Version selector */}
                  {plugin.versions && plugin.versions.length > 1 ? (
                    <div className="flex items-center gap-1">
                      <select
                        value={urlVersion || plugin.versions[0]?.version}
                        onChange={(e) => {
                          const selectedVersion = e.target.value;
                          if (selectedVersion === plugin.versions[0]?.version) {
                            navigate(`/plugin/${plugin.slug}`);
                          } else {
                            navigate(`/plugin/${plugin.slug}/v/${selectedVersion}`);
                          }
                        }}
                        className="rounded-md bg-elevated px-2 py-0.5 font-mono text-xs text-secondary ring-1 ring-surface focus:outline-none focus:ring-amber"
                      >
                        {plugin.versions.map((v, index) => (
                          <option key={v.id} value={v.version}>
                            v{v.version}
                            {index === 0 ? " (latest)" : ""}
                          </option>
                        ))}
                      </select>
                      <span className="font-mono text-xs text-muted">
                        by {plugin.authorName || plugin.author.name}
                      </span>
                    </div>
                  ) : (
                    <span className="font-mono text-xs text-muted">
                      v{plugin.versions?.[0]?.version || "1.0.0"} by{" "}
                      {plugin.authorName || plugin.author.name}
                    </span>
                  )}
                  {plugin.forkedFrom && (
                    <>
                      <span className="text-elevated">|</span>
                      <Link
                        to={`/plugin/${plugin.forkedFrom.slug}`}
                        className="flex items-center gap-1 font-mono text-xs text-muted transition-colors hover:text-secondary"
                      >
                        <ForkIcon size={12} />
                        Forked from {plugin.forkedFrom.name} by{" "}
                        {plugin.forkedFrom.authorName || plugin.forkedFrom.author.name}
                      </Link>
                    </>
                  )}
                  <span className="text-elevated">|</span>
                  <div className="flex items-center gap-1.5">
                    {Object.entries(fileTypeCounts)
                      .filter(([type]) => type !== "OTHER")
                      .map(([type, count]) => {
                        const config = fileTypeConfig[type];
                        if (!config) return null;
                        return (
                          <span
                            key={type}
                            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ${config.bgColor} ${config.color}`}
                            title={`${count} ${config.label}${count > 1 ? "s" : ""}`}
                          >
                            <config.Icon size={10} />
                            {count}
                          </span>
                        );
                      })}
                  </div>
                </div>
                {plugin.description && (
                  <p className="mt-2 max-w-2xl text-sm text-secondary">{plugin.description}</p>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-4">
              <div className="hidden items-center gap-2 sm:flex sm:gap-4">
                <div className="flex items-center gap-1.5 text-sm text-muted">
                  <DownloadIcon size={14} />
                  <span className="font-mono">{plugin._count?.downloads || 0}</span>
                </div>
                <button
                  onClick={handleToggleFavorite}
                  disabled={!isAuthenticated || isTogglingFavorite}
                  className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm transition-colors ${
                    isFavorited
                      ? "text-amber hover:text-amber-hover"
                      : "text-muted hover:bg-surface hover:text-secondary"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                  title={isAuthenticated ? (isFavorited ? "Unstar" : "Star") : "Sign in to star"}
                >
                  <StarIcon
                    size={14}
                    filled={isFavorited}
                    className={isFavorited ? "text-amber" : ""}
                  />
                  <span className="font-mono">{favoriteCount}</span>
                </button>
                <div className="flex items-center gap-1.5 text-sm text-muted">
                  <ForkIcon size={14} />
                  <span className="font-mono">{plugin._count?.forks || 0}</span>
                </div>
              </div>

              {(plugin.status === "APPROVED" || plugin.status === "PUBLISHED") && (
                <>
                  {isPaidPlugin && plugin.price && (
                    <span className="rounded-full bg-success/10 px-3 py-1 font-mono text-sm font-medium text-success">
                      {formatPrice(plugin.price)}
                    </span>
                  )}
                  {isPaidPlugin && !isPurchased && !isOwner ? (
                    <button
                      onClick={isAuthenticated ? handleBuy : () => navigate("/login")}
                      disabled={isBuying}
                      className="flex items-center gap-2 rounded-lg bg-success px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-success/80 disabled:opacity-50"
                    >
                      {isBuying ? (
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                      ) : (
                        <span>Buy Now</span>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowInstallModal(true)}
                      className="flex items-center gap-2 rounded-lg bg-amber px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-amber-hover"
                    >
                      <DownloadIcon size={14} />
                      <span>{isPurchased ? "Purchased — Install" : "Install"}</span>
                    </button>
                  )}
                </>
              )}

              <button
                onClick={() => setShowInfoModal(true)}
                title="Plugin info"
                className="flex items-center justify-center rounded-lg bg-surface p-2 text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary"
              >
                <InfoIcon size={18} />
              </button>

              {plugin.repositoryUrl && (
                <a
                  href={plugin.repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View repository"
                  className="flex items-center justify-center rounded-lg bg-surface p-2 text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary"
                >
                  <GitHubIcon size={18} />
                </a>
              )}

              {canFork && (
                <button
                  onClick={handleFork}
                  disabled={isForking}
                  className="flex items-center gap-2 rounded-lg bg-surface px-3 py-1.5 text-sm text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isForking ? (
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted border-t-transparent" />
                  ) : (
                    <ForkIcon size={14} />
                  )}
                  <span className="hidden sm:inline">{isForking ? "Forking..." : "Fork"}</span>
                </button>
              )}

              {isOwner && plugin.status === "DRAFT" && (
                <button
                  onClick={handleSubmitForReview}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 rounded-lg bg-success px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-success/80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                  ) : (
                    <CheckIcon size={14} />
                  )}
                  <span className="hidden sm:inline">
                    {isSubmitting ? "Submitting..." : "Submit for Approval"}
                  </span>
                </button>
              )}

              {isOwner && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 rounded-lg bg-amber px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-amber-hover"
                >
                  <PlusIcon size={14} />
                  <span className="hidden sm:inline">Add</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Success Banner */}
      {showPurchaseSuccess && (
        <div className="relative z-10 border-b border-success/20 bg-success/10">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <CheckIcon size={16} className="text-success" />
              <span className="text-sm font-medium text-success">
                Purchase complete! You can now install this plugin.
              </span>
            </div>
            <button
              onClick={() => setShowPurchaseSuccess(false)}
              className="text-success/60 hover:text-success"
            >
              <XIcon size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="relative z-10 border-b border-surface bg-surface/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("overview")}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "overview"
                  ? "border-amber text-amber"
                  : "border-transparent text-muted hover:text-secondary"
              }`}
            >
              <GridIcon size={16} />
              Overview
            </button>
            <button
              onClick={() => setActiveTab("files")}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "files"
                  ? "border-amber text-amber"
                  : "border-transparent text-muted hover:text-secondary"
              }`}
            >
              <ListIcon size={16} />
              File Explorer
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="relative z-10 flex flex-1 overflow-hidden">
        {activeTab === "overview" ? (
          /* Overview Tab */
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
              {/* Two-column layout: Components left, README right */}
              <div className="flex flex-col gap-8 lg:flex-row">
                {/* Left column: Components */}
                <div className="flex-1 lg:max-w-xl">
                  {/* Cards Section */}
                  {hasCards ? (
                    <div className="space-y-8">
                      {cardTypes.map((type) => {
                        const files = filesByType[type];
                        if (!files || files.length === 0) return null;
                        const config = fileTypeConfig[type];

                        return (
                          <div key={type}>
                            <h3
                              className={`mb-4 flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-wider ${config.color}`}
                            >
                              <config.Icon size={14} />
                              {config.label}s ({files.length})
                            </h3>
                            <div className="flex flex-col gap-3">
                              {files.map((file) => {
                                const meta = file.metadata as PluginFileMetadata | null;
                                // Prefer name from metadata, then displayName, then derive from filename
                                // For generic filenames like SKILL.md, use parent directory name
                                const getFileDisplayName = () => {
                                  const metaName = safeString(meta?.name);
                                  if (metaName) return metaName;
                                  if (file.displayName) return file.displayName;
                                  const parts = file.fileName.split("/");
                                  const fileName = parts.pop() || "";
                                  const baseName = fileName.replace(/\.(md|json)$/, "");
                                  if (
                                    ["SKILL", "COMMAND", "AGENT", "HOOK"].includes(
                                      baseName.toUpperCase()
                                    )
                                  ) {
                                    const parentDir = parts.pop();
                                    if (parentDir) return parentDir;
                                  }
                                  return baseName || file.fileName;
                                };
                                const displayName = getFileDisplayName();
                                // Prefer description from metadata
                                const description =
                                  safeString(meta?.description) || file.description;
                                const isCommand = type === "COMMAND";
                                const isSkill = type === "SKILL";
                                const isAgent = type === "AGENT";

                                return (
                                  <div
                                    key={file.id}
                                    className="group relative rounded-xl border border-elevated bg-surface/50 p-4 transition-all hover:border-elevated hover:bg-surface hover:ring-1 hover:ring-elevated"
                                  >
                                    <button
                                      onClick={() => openSlideout(file)}
                                      className="w-full text-left"
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className={`rounded-lg p-2 ${config.bgColor}`}>
                                          <config.Icon size={18} className={config.color} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            {isCommand ? (
                                              <p className="font-mono text-sm font-medium text-blue-400">
                                                /{plugin?.slug}:
                                                {displayName.toLowerCase().replace(/\s+/g, "-")}
                                              </p>
                                            ) : (
                                              <p className="font-mono text-sm font-medium text-primary group-hover:text-white">
                                                {displayName}
                                              </p>
                                            )}
                                            {(isSkill || isAgent) && safeString(meta?.version) && (
                                              <span className="rounded bg-purple-500/20 px-1.5 py-0.5 font-mono text-xs text-purple-400">
                                                v{safeString(meta?.version)}
                                              </span>
                                            )}
                                            {safeString(meta?.model) && (
                                              <span className="rounded bg-elevated px-1.5 py-0.5 font-mono text-xs text-muted">
                                                {safeString(meta?.model)}
                                              </span>
                                            )}
                                          </div>
                                          {/* Argument hints for commands */}
                                          {isCommand && safeString(meta?.argument_hints) && (
                                            <p className="mt-1 font-mono text-xs text-muted">
                                              {safeString(meta?.argument_hints)}
                                            </p>
                                          )}
                                          {description && (
                                            <p className="mt-2 line-clamp-2 text-sm text-muted">
                                              {description}
                                            </p>
                                          )}
                                          {/* Allowed tools (for skills/commands) */}
                                          {meta?.allowed_tools && meta.allowed_tools.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-1">
                                              {meta.allowed_tools.slice(0, 4).map((tool, i) => (
                                                <span
                                                  key={i}
                                                  className="rounded bg-elevated px-1.5 py-0.5 font-mono text-xs text-secondary"
                                                >
                                                  {tool}
                                                </span>
                                              ))}
                                              {meta.allowed_tools.length > 4 && (
                                                <span className="text-xs text-muted">
                                                  +{meta.allowed_tools.length - 4} more
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          {/* Tools (for agents) */}
                                          {isAgent && meta?.tools && meta.tools.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-1">
                                              {(meta.tools as string[])
                                                .slice(0, 4)
                                                .map((tool, i) => (
                                                  <span
                                                    key={i}
                                                    className="rounded bg-amber/20 px-1.5 py-0.5 font-mono text-xs text-amber"
                                                  >
                                                    {tool}
                                                  </span>
                                                ))}
                                              {(meta.tools as string[]).length > 4 && (
                                                <span className="text-xs text-muted">
                                                  +{(meta.tools as string[]).length - 4} more
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <ChevronRightIcon
                                          size={16}
                                          className="shrink-0 text-muted transition-transform group-hover:translate-x-1 group-hover:text-secondary"
                                        />
                                      </div>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-elevated bg-surface/30 p-8 text-center">
                      <FileIcon size={48} className="mx-auto mb-4 text-elevated" />
                      {isOwner ? (
                        <>
                          <p className="text-lg font-medium text-secondary">
                            Add your first component
                          </p>
                          <p className="mt-2 text-muted">
                            Create skills, commands, agents, or hooks to bring your plugin to life.
                          </p>
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="btn-primary mt-6 inline-flex"
                          >
                            <PlusIcon size={16} />
                            Add
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="text-muted">
                            No skills, commands, or agents found in this plugin.
                          </p>
                          <button
                            onClick={() => setActiveTab("files")}
                            className="mt-4 text-sm text-amber hover:text-amber-hover"
                          >
                            View all files →
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Right column: README */}
                {readmeContent && (
                  <div className="flex-1">
                    <h2 className="mb-4 flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                      <FileIcon size={14} />
                      README
                    </h2>
                    <div className="rounded-xl border border-elevated bg-surface/30 p-6">
                      <div className="prose prose-sm prose-invert max-w-none prose-headings:text-primary prose-p:text-secondary prose-a:text-accent prose-strong:text-primary prose-code:rounded prose-code:bg-elevated prose-code:px-1 prose-code:py-0.5 prose-code:text-accent prose-pre:bg-elevated prose-pre:border prose-pre:border-elevated prose-li:text-secondary prose-img:rounded-lg prose-img:my-4">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
                          components={{
                            img: ({ src, alt, ...props }) => {
                              // Resolve relative image paths to S3 URLs
                              let resolvedSrc = src || "";
                              if (src && !src.startsWith("http") && plugin?.files) {
                                const matchingFile = plugin.files.find(
                                  (f) => f.fileName === src || f.fileName.endsWith(`/${src}`)
                                );
                                if (matchingFile) {
                                  resolvedSrc = matchingFile.s3Url;
                                }
                              }
                              return (
                                <img
                                  src={resolvedSrc}
                                  alt={alt || ""}
                                  className="max-w-full rounded-lg"
                                  {...props}
                                />
                              );
                            },
                          }}
                        >
                          {readmeContent}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Review Note (if rejected) */}
              {plugin.status === "REJECTED" && plugin.reviewNote && (
                <div className="mt-6 rounded-xl border border-danger/30 bg-danger/5 p-6">
                  <h3 className="mb-2 font-mono text-xs font-medium uppercase tracking-wider text-danger">
                    Review Feedback
                  </h3>
                  <p className="text-sm text-danger/80">{plugin.reviewNote}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* File Explorer Tab */
          <>
            {/* Left Sidebar - File Tree */}
            <div className="w-72 shrink-0 overflow-y-auto border-r border-surface bg-surface/50">
              <div className="p-4">
                <h2 className="mb-3 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                  Files
                </h2>

                {fileTree.length === 0 ? (
                  <p className="text-sm text-muted">No files</p>
                ) : (
                  <div className="space-y-0.5">{renderFileTree(fileTree)}</div>
                )}
              </div>
            </div>

            {/* Main Content - File Viewer */}
            <div className="flex flex-1 flex-col overflow-hidden">
              {selectedFile ? (
                <>
                  <div className="flex items-center justify-between border-b border-surface bg-surface/30 px-4 py-2">
                    <div className="flex items-center gap-3">
                      <FileIcon size={16} className="text-muted" />
                      <span className="font-mono text-sm text-secondary">
                        {selectedFile.fileName}
                      </span>
                      {selectedFile.displayName &&
                        selectedFile.displayName !== selectedFile.fileName.split("/").pop() && (
                          <span className="rounded bg-amber/20 px-1.5 py-0.5 font-mono text-xs text-amber">
                            {selectedFile.displayName}
                          </span>
                        )}
                      <span className="font-mono text-xs text-muted">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={copyFileContent}
                        className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted transition-colors hover:bg-surface hover:text-secondary"
                      >
                        {copied === "file" ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
                        {copied === "file" ? "Copied" : "Copy"}
                      </button>
                      <a
                        href={selectedFile.s3Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted transition-colors hover:bg-surface hover:text-secondary"
                      >
                        <ExternalLinkIcon size={12} />
                        Raw
                      </a>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto bg-base p-4">
                    {loadingContent ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                      </div>
                    ) : (
                      <pre className="font-mono text-sm leading-relaxed text-secondary whitespace-pre-wrap">
                        <code>{fileContent}</code>
                      </pre>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <div className="text-center">
                    <FileIcon size={48} className="mx-auto mb-4 text-elevated" />
                    <p className="font-mono text-sm text-muted">
                      {plugin.files?.length === 0
                        ? "No files in this plugin"
                        : "Select a file to view its contents"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Slideout Panel */}
      {slideoutFile && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-base/60 backdrop-blur-sm animate-fade-in"
            onClick={closeSlideout}
          />

          {/* Panel */}
          <div className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-2xl flex-col overflow-hidden border-l border-elevated bg-surface shadow-2xl animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-surface px-6 py-4">
              <div className="flex items-center gap-3">
                {(() => {
                  const config = fileTypeConfig[slideoutFile.fileType];
                  if (!config) return null;
                  return (
                    <div className={`rounded-lg p-2 ${config.bgColor}`}>
                      <config.Icon size={18} className={config.color} />
                    </div>
                  );
                })()}
                <div>
                  <h2 className="font-mono text-lg font-medium text-primary">
                    {slideoutFile.displayName ||
                      slideoutFile.fileName
                        .split("/")
                        .pop()
                        ?.replace(/\.(md|json)$/, "")}
                  </h2>
                  {slideoutFile.description && (
                    <p className="mt-0.5 text-sm text-muted">{slideoutFile.description}</p>
                  )}
                </div>
              </div>
              <button
                onClick={closeSlideout}
                className="rounded-lg p-2 text-muted transition-colors hover:bg-surface hover:text-secondary"
              >
                <XIcon size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loadingSlideout ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                </div>
              ) : (
                <div className="p-6">
                  {/* Metadata Section */}
                  {(() => {
                    const meta = slideoutFile.metadata as PluginFileMetadata | null;
                    if (!meta) return null;

                    const hasMetadata =
                      meta.description ||
                      meta.argument_hints ||
                      meta.allowed_tools?.length ||
                      meta.tools?.length ||
                      meta.permissionMode ||
                      meta.skills?.length ||
                      meta.color ||
                      meta.model ||
                      meta.version;

                    if (!hasMetadata) return null;

                    return (
                      <div className="mb-6 space-y-4 rounded-xl border border-elevated bg-surface/50 p-4">
                        {safeString(meta.description) && (
                          <div>
                            <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                              Description
                            </dt>
                            <dd className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap text-sm text-secondary">
                              {safeString(meta.description)}
                            </dd>
                          </div>
                        )}
                        {safeString(meta.argument_hints) && (
                          <div>
                            <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                              Arguments
                            </dt>
                            <dd className="mt-1 font-mono text-sm text-secondary">
                              {safeString(meta.argument_hints)}
                            </dd>
                          </div>
                        )}
                        {meta.allowed_tools && meta.allowed_tools.length > 0 && (
                          <div>
                            <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                              Allowed Tools
                            </dt>
                            <dd className="mt-2 flex flex-wrap gap-1.5">
                              {meta.allowed_tools.map((tool, i) => (
                                <span
                                  key={i}
                                  className="rounded-full bg-blue-500/20 px-2 py-0.5 font-mono text-xs text-blue-400 ring-1 ring-blue-500/30"
                                >
                                  {tool}
                                </span>
                              ))}
                            </dd>
                          </div>
                        )}
                        {/* Agent tools */}
                        {meta.tools && (meta.tools as string[]).length > 0 && (
                          <div>
                            <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                              Tools
                            </dt>
                            <dd className="mt-2 flex flex-wrap gap-1.5">
                              {(meta.tools as string[]).map((tool, i) => (
                                <span
                                  key={i}
                                  className="rounded-full bg-amber/20 px-2 py-0.5 font-mono text-xs text-amber ring-1 ring-amber/30"
                                >
                                  {tool}
                                </span>
                              ))}
                            </dd>
                          </div>
                        )}
                        {/* Agent skills */}
                        {meta.skills && (meta.skills as string[]).length > 0 && (
                          <div>
                            <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                              Skills
                            </dt>
                            <dd className="mt-2 flex flex-wrap gap-1.5">
                              {(meta.skills as string[]).map((skill, i) => (
                                <span
                                  key={i}
                                  className="rounded-full bg-purple-500/20 px-2 py-0.5 font-mono text-xs text-purple-400 ring-1 ring-purple-500/30"
                                >
                                  {skill}
                                </span>
                              ))}
                            </dd>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-6">
                          {safeString(meta.model) && (
                            <div>
                              <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                                Model
                              </dt>
                              <dd className="mt-1 font-mono text-sm text-secondary">
                                {safeString(meta.model)}
                              </dd>
                            </div>
                          )}
                          {safeString(meta.version) && (
                            <div>
                              <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                                Version
                              </dt>
                              <dd className="mt-1 font-mono text-sm text-secondary">
                                {safeString(meta.version)}
                              </dd>
                            </div>
                          )}
                          {safeString(meta.permissionMode) && (
                            <div>
                              <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                                Permission Mode
                              </dt>
                              <dd className="mt-1 font-mono text-sm text-secondary">
                                {safeString(meta.permissionMode)}
                              </dd>
                            </div>
                          )}
                          {safeString(meta.color) && (
                            <div>
                              <dt className="font-mono text-xs uppercase tracking-wider text-muted">
                                Color
                              </dt>
                              <dd className="mt-1 flex items-center gap-2">
                                <span
                                  className="h-4 w-4 rounded"
                                  style={{ backgroundColor: safeString(meta.color) || undefined }}
                                />
                                <span className="font-mono text-sm text-secondary">
                                  {safeString(meta.color)}
                                </span>
                              </dd>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* File Content */}
                  <div>
                    <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
                      Instructions
                    </h3>
                    <pre className="whitespace-pre-wrap rounded-xl bg-base p-4 font-mono text-sm leading-relaxed text-secondary">
                      {slideoutContent}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Delete button for owners */}
            {isOwner && (
              <div className="border-t border-surface p-4">
                <button
                  onClick={() => {
                    handleDeleteFile(slideoutFile.id);
                    closeSlideout();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-4 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger/20"
                >
                  <TrashIcon size={16} />
                  Delete Component
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Component Modal */}
      {showAddModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-base/60 backdrop-blur-sm animate-fade-in"
            onClick={resetAddModal}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-2xl rounded-2xl border border-elevated bg-surface shadow-2xl animate-fade-in my-8">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-elevated p-6">
                <div className="flex items-center gap-3">
                  {selectedComponentType && (
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        selectedComponentType === "SKILL"
                          ? "bg-purple-500/20"
                          : selectedComponentType === "COMMAND"
                            ? "bg-blue-500/20"
                            : selectedComponentType === "AGENT"
                              ? "bg-amber/20"
                              : "bg-success/20"
                      }`}
                    >
                      {selectedComponentType === "SKILL" && (
                        <SparklesIcon size={20} className="text-purple-400" />
                      )}
                      {selectedComponentType === "COMMAND" && (
                        <TerminalIcon size={20} className="text-blue-400" />
                      )}
                      {selectedComponentType === "AGENT" && (
                        <BotIcon size={20} className="text-amber" />
                      )}
                      {selectedComponentType === "HOOK" && (
                        <ZapIcon size={20} className="text-success" />
                      )}
                    </div>
                  )}
                  <div>
                    <h2 className="font-display text-xl font-bold text-primary">
                      {selectedComponentType
                        ? `Create ${selectedComponentType.charAt(0) + selectedComponentType.slice(1).toLowerCase()}`
                        : "Add"}
                    </h2>
                    <p className="mt-1 text-sm text-muted">
                      {selectedComponentType
                        ? "Configure your component settings"
                        : "Choose what type of component to add"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetAddModal}
                  className="rounded-lg p-2 text-muted hover:bg-elevated hover:text-secondary"
                >
                  <XIcon size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="max-h-[70vh] overflow-y-auto p-6">
                {!selectedComponentType ? (
                  /* Component Type Selection */
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      onClick={() => setSelectedComponentType("SKILL")}
                      className="group rounded-xl border border-elevated bg-surface/50 p-4 text-left transition-all hover:border-purple-500/50 hover:bg-surface"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/20">
                          <SparklesIcon size={20} className="text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">Skill</p>
                          <p className="text-xs text-muted">Teach Claude new capabilities</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedComponentType("COMMAND")}
                      className="group rounded-xl border border-elevated bg-surface/50 p-4 text-left transition-all hover:border-blue-500/50 hover:bg-surface"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/20">
                          <TerminalIcon size={20} className="text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">Command</p>
                          <p className="text-xs text-muted">Create slash commands</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedComponentType("AGENT")}
                      className="group rounded-xl border border-elevated bg-surface/50 p-4 text-left transition-all hover:border-amber/50 hover:bg-surface"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber/20">
                          <BotIcon size={20} className="text-amber" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">Agent</p>
                          <p className="text-xs text-muted">Define specialized subagents</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedComponentType("HOOK")}
                      className="group rounded-xl border border-elevated bg-surface/50 p-4 text-left transition-all hover:border-success/50 hover:bg-surface"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/20">
                          <ZapIcon size={20} className="text-success" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">Hook</p>
                          <p className="text-xs text-muted">Add automation triggers</p>
                        </div>
                      </div>
                    </button>
                  </div>
                ) : selectedComponentType === "SKILL" ? (
                  /* Skill Form */
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Name *</label>
                        <input
                          type="text"
                          value={skillForm.name}
                          onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                          placeholder="My Skill"
                          className="input-field"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Model</label>
                        <select
                          value={skillForm.model}
                          onChange={(e) => setSkillForm({ ...skillForm, model: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Default</option>
                          <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                          <option value="claude-opus-4-20250514">Claude Opus 4</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={skillForm.description}
                        onChange={(e) =>
                          setSkillForm({ ...skillForm, description: e.target.value })
                        }
                        placeholder="Brief description of what this skill does"
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Allowed Tools
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = skillForm.allowedTools.includes(tool)
                                ? skillForm.allowedTools.filter((t) => t !== tool)
                                : [...skillForm.allowedTools, tool];
                              setSkillForm({ ...skillForm, allowedTools: tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              skillForm.allowedTools.includes(tool)
                                ? "bg-purple-500/30 text-purple-300 ring-1 ring-purple-500/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Skill Instructions *
                      </label>
                      <textarea
                        value={skillForm.content}
                        onChange={(e) => setSkillForm({ ...skillForm, content: e.target.value })}
                        rows={10}
                        className="input-field resize-none font-mono text-xs"
                      />
                    </div>

                    {/* Reference Files */}
                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Reference Files (optional)
                      </label>
                      <p className="mb-2 text-xs text-muted">
                        Additional files that will be placed in the skill folder
                      </p>
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-elevated bg-surface/30 p-4 transition-colors hover:border-purple-500/50 hover:bg-surface/50">
                        <UploadIcon size={16} className="text-muted" />
                        <span className="font-mono text-sm text-muted">Click to upload files</span>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleReferenceFileUpload}
                        />
                      </label>
                      {referenceFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {referenceFiles.map((file, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between rounded bg-elevated px-2 py-1"
                            >
                              <span className="font-mono text-xs text-secondary">{file.name}</span>
                              <button
                                onClick={() => removeReferenceFile(i)}
                                className="text-muted hover:text-danger"
                              >
                                <TrashIcon size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : selectedComponentType === "COMMAND" ? (
                  /* Command Form */
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Command Name *
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted">/</span>
                          <input
                            type="text"
                            value={commandForm.name}
                            onChange={(e) =>
                              setCommandForm({
                                ...commandForm,
                                name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                              })
                            }
                            placeholder="my-command"
                            className="input-field flex-1"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Argument Hint
                        </label>
                        <input
                          type="text"
                          value={commandForm.argumentHint}
                          onChange={(e) =>
                            setCommandForm({ ...commandForm, argumentHint: e.target.value })
                          }
                          placeholder="[file] [options]"
                          className="input-field"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Description
                        </label>
                        <input
                          type="text"
                          value={commandForm.description}
                          onChange={(e) =>
                            setCommandForm({ ...commandForm, description: e.target.value })
                          }
                          placeholder="Brief description"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Model</label>
                        <select
                          value={commandForm.model}
                          onChange={(e) =>
                            setCommandForm({ ...commandForm, model: e.target.value })
                          }
                          className="input-field"
                        >
                          <option value="">Default</option>
                          <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                          <option value="claude-opus-4-20250514">Claude Opus 4</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Allowed Tools
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = commandForm.allowedTools.includes(tool)
                                ? commandForm.allowedTools.filter((t) => t !== tool)
                                : [...commandForm.allowedTools, tool];
                              setCommandForm({ ...commandForm, allowedTools: tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              commandForm.allowedTools.includes(tool)
                                ? "bg-blue-500/30 text-blue-300 ring-1 ring-blue-500/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Command Instructions *
                      </label>
                      <textarea
                        value={commandForm.content}
                        onChange={(e) =>
                          setCommandForm({ ...commandForm, content: e.target.value })
                        }
                        rows={8}
                        className="input-field resize-none font-mono text-xs"
                      />
                      <p className="mt-1 text-xs text-muted">
                        Use $ARGUMENTS for all arguments or $1, $2 for individual arguments
                      </p>
                    </div>
                  </div>
                ) : selectedComponentType === "AGENT" ? (
                  /* Agent Form */
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Name *</label>
                        <input
                          type="text"
                          value={agentForm.name}
                          onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                          placeholder="my-agent"
                          className="input-field"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Model</label>
                        <select
                          value={agentForm.model}
                          onChange={(e) => setAgentForm({ ...agentForm, model: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Default</option>
                          <option value="sonnet">Sonnet (fast)</option>
                          <option value="opus">Opus (powerful)</option>
                          <option value="haiku">Haiku (quick)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Description *
                        </label>
                        <input
                          type="text"
                          value={agentForm.description}
                          onChange={(e) =>
                            setAgentForm({ ...agentForm, description: e.target.value })
                          }
                          placeholder="What this agent does"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Permission Mode
                        </label>
                        <select
                          value={agentForm.permissionMode}
                          onChange={(e) =>
                            setAgentForm({ ...agentForm, permissionMode: e.target.value })
                          }
                          className="input-field"
                        >
                          <option value="default">Default</option>
                          <option value="acceptEdits">Accept Edits</option>
                          <option value="bypassPermissions">Bypass Permissions</option>
                          <option value="plan">Plan Mode</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Available Tools
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = agentForm.tools.includes(tool)
                                ? agentForm.tools.filter((t) => t !== tool)
                                : [...agentForm.tools, tool];
                              setAgentForm({ ...agentForm, tools: tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              agentForm.tools.includes(tool)
                                ? "bg-amber/30 text-amber ring-1 ring-amber/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        System Prompt *
                      </label>
                      <textarea
                        value={agentForm.content}
                        onChange={(e) => setAgentForm({ ...agentForm, content: e.target.value })}
                        rows={10}
                        className="input-field resize-none font-mono text-xs"
                      />
                    </div>
                  </div>
                ) : (
                  /* Hook Form */
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Event *</label>
                        <select
                          value={hookForm.event}
                          onChange={(e) => setHookForm({ ...hookForm, event: e.target.value })}
                          className="input-field"
                        >
                          {HOOK_EVENTS.map((event) => (
                            <option key={event.value} value={event.value}>
                              {event.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Matcher (optional)
                        </label>
                        <input
                          type="text"
                          value={hookForm.matcher}
                          onChange={(e) => setHookForm({ ...hookForm, matcher: e.target.value })}
                          placeholder="Write|Edit"
                          className="input-field"
                        />
                        <p className="mt-1 text-xs text-muted">Regex pattern to match tool names</p>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">Hook Type *</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setHookForm({ ...hookForm, hookType: "command" })}
                          className={`flex-1 rounded-lg p-3 text-left transition-colors ${
                            hookForm.hookType === "command"
                              ? "bg-success/20 ring-1 ring-success/50"
                              : "bg-elevated hover:bg-elevated/80"
                          }`}
                        >
                          <p className="font-mono text-sm font-medium text-primary">Command</p>
                          <p className="text-xs text-muted">Run a bash script</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setHookForm({ ...hookForm, hookType: "prompt" })}
                          className={`flex-1 rounded-lg p-3 text-left transition-colors ${
                            hookForm.hookType === "prompt"
                              ? "bg-success/20 ring-1 ring-success/50"
                              : "bg-elevated hover:bg-elevated/80"
                          }`}
                        >
                          <p className="font-mono text-sm font-medium text-primary">Prompt</p>
                          <p className="text-xs text-muted">LLM evaluation</p>
                        </button>
                      </div>
                    </div>

                    {hookForm.hookType === "command" ? (
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Command *</label>
                        <input
                          type="text"
                          value={hookForm.command}
                          onChange={(e) => setHookForm({ ...hookForm, command: e.target.value })}
                          placeholder="./scripts/validate.sh"
                          className="input-field font-mono"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Prompt *</label>
                        <textarea
                          value={hookForm.prompt}
                          onChange={(e) => setHookForm({ ...hookForm, prompt: e.target.value })}
                          rows={6}
                          placeholder="Validate that the tool use follows best practices..."
                          className="input-field resize-none font-mono text-xs"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              {selectedComponentType && (
                <div className="flex items-center gap-3 border-t border-elevated p-6">
                  <button
                    onClick={handleCreateComponent}
                    disabled={
                      isUploading ||
                      (selectedComponentType === "SKILL"
                        ? !skillForm.name || !skillForm.description
                        : selectedComponentType === "COMMAND"
                          ? !commandForm.name
                          : selectedComponentType === "AGENT"
                            ? !agentForm.name || !agentForm.description
                            : selectedComponentType === "HOOK"
                              ? hookForm.hookType === "command"
                                ? !hookForm.command
                                : !hookForm.prompt
                              : true)
                    }
                    className="btn-primary flex-1"
                  >
                    {isUploading ? "Creating..." : "Create"}
                  </button>
                  <button
                    onClick={() => setSelectedComponentType(null)}
                    className="btn-secondary flex-1"
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Install Modal */}
      {showInstallModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-base/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowInstallModal(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-xl rounded-2xl border border-elevated bg-surface shadow-2xl animate-fade-in my-8">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-elevated p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber/20">
                    <DownloadIcon size={20} className="text-amber" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-bold text-primary">Install Plugin</h2>
                    <p className="mt-1 text-sm text-muted">
                      Follow these steps to install {plugin?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInstallModal(false)}
                  className="rounded-lg p-2 text-muted hover:bg-elevated hover:text-secondary"
                >
                  <XIcon size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Step 1: Add Marketplace */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber/20 text-xs font-bold text-amber">
                      1
                    </span>
                    <h3 className="font-medium text-primary">Add the marketplace</h3>
                  </div>
                  <p className="text-sm text-muted ml-8">
                    If you haven't added this marketplace yet, run this command first:
                  </p>
                  <div className="flex items-center gap-2 ml-8">
                    <code className="flex-1 overflow-x-auto rounded-lg bg-base px-3 py-2 font-mono text-sm text-secondary ring-1 ring-elevated">
                      {addMarketplaceCommand}
                    </code>
                    <button
                      onClick={() => copyToClipboard(addMarketplaceCommand, "add-marketplace")}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-surface px-3 py-2 text-xs text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary"
                    >
                      {copied === "add-marketplace" ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                      {copied === "add-marketplace" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>

                {/* Step 2: Update Marketplace */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber/20 text-xs font-bold text-amber">
                      2
                    </span>
                    <h3 className="font-medium text-primary">Update the marketplace</h3>
                  </div>
                  <p className="text-sm text-muted ml-8">
                    If the plugin isn't available yet, refresh to get the latest plugins:
                  </p>
                  <div className="flex items-center gap-2 ml-8">
                    <code className="flex-1 overflow-x-auto rounded-lg bg-base px-3 py-2 font-mono text-sm text-secondary ring-1 ring-elevated">
                      {updateMarketplaceCommand}
                    </code>
                    <button
                      onClick={() =>
                        copyToClipboard(updateMarketplaceCommand, "update-marketplace")
                      }
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-surface px-3 py-2 text-xs text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary"
                    >
                      {copied === "update-marketplace" ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                      {copied === "update-marketplace" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>

                {/* Step 3: Install Plugin */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber/20 text-xs font-bold text-amber">
                      3
                    </span>
                    <h3 className="font-medium text-primary">Install the plugin</h3>
                  </div>
                  <p className="text-sm text-muted ml-8">
                    Finally, install the plugin with this command:
                  </p>
                  <div className="flex items-center gap-2 ml-8">
                    <code className="flex-1 overflow-x-auto rounded-lg bg-base px-3 py-2 font-mono text-sm text-secondary ring-1 ring-elevated">
                      {installPluginCommand}
                    </code>
                    <button
                      onClick={() => copyToClipboard(installPluginCommand, "install-plugin")}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-surface px-3 py-2 text-xs text-secondary ring-1 ring-elevated transition-colors hover:bg-elevated hover:text-primary"
                    >
                      {copied === "install-plugin" ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                      {copied === "install-plugin" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>

                {/* Quick Install */}
                <div className="border-t border-elevated pt-6">
                  <div className="space-y-3">
                    <h3 className="font-medium text-primary">Quick install (one command)</h3>
                    <p className="text-sm text-muted">
                      Already have the marketplace? Run this single command:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 overflow-x-auto rounded-lg bg-base px-3 py-2 font-mono text-sm text-secondary ring-1 ring-elevated">
                        {installCommand}
                      </code>
                      <button
                        onClick={() => copyToClipboard(installCommand, "install")}
                        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-amber px-3 py-2 text-xs font-medium text-black transition-colors hover:bg-amber-hover"
                      >
                        {copied === "install" ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                        {copied === "install" ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-base/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowInfoModal(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-lg rounded-2xl border border-elevated bg-surface shadow-2xl animate-fade-in my-8">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-elevated p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber/20">
                    <InfoIcon size={20} className="text-amber" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-bold text-primary">Plugin Info</h2>
                    <p className="mt-1 text-sm text-muted">{plugin?.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="rounded-lg p-2 text-muted hover:bg-elevated hover:text-secondary"
                >
                  <XIcon size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                {/* Plugin Details */}
                <div className="space-y-4">
                  <div>
                    <dt className="text-xs text-muted uppercase tracking-wider">Tags</dt>
                    <dd className="mt-2 flex flex-wrap gap-1">
                      {plugin.tags.length > 0 ? (
                        plugin.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded bg-claude/20 px-2 py-0.5 font-mono text-xs text-claude"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="font-mono text-sm text-muted">None</span>
                      )}
                    </dd>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs text-muted uppercase tracking-wider">Total Files</dt>
                      <dd className="mt-1 font-mono text-sm text-secondary">
                        {plugin.files?.length || 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted uppercase tracking-wider">Last Updated</dt>
                      <dd className="mt-1 font-mono text-sm text-secondary">
                        {new Date(plugin.updatedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Version History */}
                {plugin.versions && plugin.versions.length > 0 && (
                  <div>
                    <h3 className="text-xs text-muted uppercase tracking-wider mb-3">
                      Version History
                    </h3>
                    <div className="space-y-2">
                      {plugin.versions.map((v, index) => (
                        <div
                          key={v.id}
                          className={`rounded-lg border p-3 ${
                            urlVersion === v.version || (!urlVersion && index === 0)
                              ? "border-amber/30 bg-amber/5"
                              : "border-elevated bg-surface/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-medium text-primary">
                                v{v.version}
                              </span>
                              {index === 0 && (
                                <span className="rounded bg-success/20 px-1.5 py-0.5 text-xs text-success">
                                  Latest
                                </span>
                              )}
                              {(urlVersion === v.version || (!urlVersion && index === 0)) && (
                                <span className="rounded bg-amber/20 px-1.5 py-0.5 text-xs text-amber">
                                  Current
                                </span>
                              )}
                            </div>
                            <span className="font-mono text-xs text-muted">
                              {new Date(v.publishedAt).toLocaleDateString()}
                            </span>
                          </div>
                          {v.changelog && (
                            <p className="mt-2 text-sm text-secondary">{v.changelog}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
